/**
 * Search (`040-api-contract.md` → Search, `070-search-and-links.md` §§1–3). Reads only.
 *
 * One endpoint, one query. The trigram title match is folded into the same ranking
 * server-side, so there is nothing here to combine — the client sorts by nothing and
 * renders the array in the order it arrived.
 *
 * `rank` is an **opaque sort key**, not a score. The only permitted use of it is to
 * preserve the server's ordering: never render it, never threshold on it (`070` §1.2).
 * It is not even exposed on the DTO below, so that nobody can.
 */
import { apiFetch } from '@/api/client';
import type { Breadcrumb } from '@/api/endpoints/pages';

/** Which leg of the combined query matched (`070` §3). Only `body` carries a snippet. */
export type MatchType = 'title_exact' | 'title_fuzzy' | 'body';

export interface SearchPageHit {
  id: string;
  title: string;
  icon: string | null;
  /**
   * Server-produced `ts_headline`, containing `<mark>` … `</mark>` and NOTHING else —
   * the server has already `htmlspecialchars`'d the page's own text (`040`). It is still
   * treated as untrusted here: it is parsed into React elements, never handed to the DOM
   * as HTML (`070` §5, `components/SearchSnippet.tsx`).
   */
  snippet: string | null;
  breadcrumb: Breadcrumb[];
  rank: number;
  matchType?: MatchType;
}

/** Events are a secondary result class, capped at 5 server-side (`070` §1.5). M5 renders them. */
export interface SearchEventHit {
  id: string;
  title: string;
  startsAt: string;
}

export interface SearchResponse {
  pages: SearchPageHit[];
  events: SearchEventHit[];
}

/**
 * `signal` is not optional in practice — every keystroke must abort the in-flight request
 * (`070` §4, "Cancellation"), and TanStack Query hands the `queryFn` exactly this. A late
 * response overwriting a newer one is the classic palette bug.
 */
export function search(q: string, limit: number, signal?: AbortSignal): Promise<SearchResponse> {
  const params = new URLSearchParams({ q, limit: String(limit) });

  // `exactOptionalPropertyTypes` (`111` §1) means `{ signal: undefined }` and `{}` are
  // different types, and `RequestInit.signal` is `AbortSignal | null`. Build the options
  // conditionally rather than widening the client's type for one caller.
  return apiFetch<SearchResponse>(
    `/api/search?${params.toString()}`,
    signal === undefined ? {} : { signal },
  );
}
